/**
 * @file RagJobContext.jsx
 * @description Global context for RAG job progress tracking.
 *
 * Owns a single useUserSocket() instance (persistent across navigation).
 * Fetches active jobs on mount via REST, subscribes to WebSocket events,
 * and re-fetches on socket reconnect.
 *
 * Surfaces ALL active master jobs (activeJobs) so the GlobalRagProgressPanel can
 * render one bar per master. A presa launches TWO masters at once (base +
 * `(Prioritario)`); a normal collection/agent sync launches exactly ONE.
 * `activeJob` (singular) is kept as a backward-compat alias = activeJobs[0].
 *
 * File RAG chip progress is derived from polling GET /api/roles/:rolId/knowledge/status
 * every 5 s — no incremental WebSocket counters. Snapshot taken at registration time
 * so the chip shows THIS BATCH's progress, not the role's all-time count.
 *
 * @module context/RagJobContext
 */

import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
  useMemo
} from 'react';
import { useAuth } from './AuthContext';
import { useUserSocket } from '../hooks/useUserSocket';
import { supabase } from '../services/supabaseClient';
import { dispatchCreditStop } from '../services/apiClient';

const RagJobContext = createContext(null);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5678';
const FILE_RAG_POLL_MS = 5000;
// Grace window before a master-job-scoped chip (SharePoint retry) is reconciled
// closed on (re)connect — avoids racing a job we just dispatched that hasn't yet
// surfaced in /api/rag-jobs/active.
const MASTER_JOB_RECONCILE_GRACE_MS = 15000;

export const RagJobProvider = ({ children }) => {
  // ALL active master jobs (a presa launches 2 at once: base + `(Prioritario)`;
  // a normal collection/agent sync launches exactly 1). The panel renders one
  // bar per master. `activeJob` (singular) is derived below = activeJobs[0].
  const [activeJobs, setActiveJobs] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  const [lastDescriptionUpdate, setLastDescriptionUpdate] = useState(null);
  // Latest intra-file progress ping for the active folder/master job (Sistema A).
  // Raw, unfiltered { rolId, stage, batch, batchTotal } — consumers compare rolId
  // against their own activeJob.rol_id before using it (see GlobalRagProgressPanel).
  const [folderProgressDetail, setFolderProgressDetail] = useState(null);
  // File RAG vectorization tracking: { [rolId]: { total, completed, errors, _base_completed, _base_error } }
  // External shape (total/completed/errors) is stable; _base_* are internal.
  const [fileRagJobs, setFileRagJobs] = useState({});
  const [fileRagCompletionCount, setFileRagCompletionCount] = useState(0);
  const [fileRagErrorCount, setFileRagErrorCount] = useState(0);
  const [fileRagWarningCount, setFileRagWarningCount] = useState(0);
  // F13/#304/#306 — alertas de conexión por colección: { [collectionId]: { type, ...payload } }.
  // type ∈ 'context_changed' | 'deletion_quarantined' | 'new_content' | 'deletion_pending'.
  // Solo el ÚLTIMO evento por colección se conserva (suficiente para el badge/banner v1).
  const [connectionAlerts, setConnectionAlerts] = useState({});

  const { session, loading: loadingAuth } = useAuth();
  const authToken = session?.access_token;

  const { socket, isConnected: isSocketConnected } = useUserSocket();

  const isMountedRef = useRef(true);
  const prevConnectedRef = useRef(false);
  const prevRehydrateConnectedRef = useRef(false);
  const fileRagJobsRef = useRef(fileRagJobs);

  useEffect(() => {
    fileRagJobsRef.current = fileRagJobs;
  }, [fileRagJobs]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Close (remove) the file-RAG tracker bound to a master job, whatever its role.
   * SharePoint-retry chips are master-job-driven: their file never enters the
   * `file` table that /knowledge/status polling reads, so the poll could never
   * close them. The master-job terminal WS events (and reconnect reconciliation)
   * own their lifecycle instead. Mirrors the poll's close counters (#250).
   */
  const closeFileRagJobByMasterJob = useCallback((masterJobId, { hadError = false } = {}) => {
    if (!masterJobId) return;
    const match = Object.entries(fileRagJobsRef.current).find(
      ([, job]) => job?.masterJobId === masterJobId
    );
    if (!match) return;
    const [rolId] = match;
    setFileRagJobs(prev => {
      if (!prev[rolId]) return prev;
      const { [rolId]: _removed, ...rest } = prev;
      return rest;
    });
    if (hadError) setFileRagErrorCount(c => c + 1);
    setFileRagCompletionCount(c => c + 1);
  }, []);

  /**
   * Fetch active jobs for the current user (all roles)
   */
  const fetchActiveJobs = useCallback(async () => {
    if (!authToken) {
      if (isMountedRef.current) setActiveJobs([]);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/rag-jobs/active`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      if (!isMountedRef.current) return;

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          const jobs = Array.isArray(data.data) ? data.data : [];
          setActiveJobs(jobs);
          // Reconcile SharePoint-retry chips after (re)connect: if a tracked
          // master job is no longer active, its terminal WS event was missed
          // (e.g. we were disconnected) — close the chip. (#250)
          const activeIds = new Set(jobs.map(j => j.id));
          for (const job of Object.values(fileRagJobsRef.current)) {
            if (!job?.masterJobId || activeIds.has(job.masterJobId)) continue;
            if (job.createdAt && Date.now() - job.createdAt < MASTER_JOB_RECONCILE_GRACE_MS) continue;
            closeFileRagJobByMasterJob(job.masterJobId, { hadError: false });
          }
        }
      }
    } catch (err) {
      console.error('[RagJobContext] Error fetching active jobs:', err);
    }
  }, [authToken, closeFileRagJobByMasterJob]);

  /**
   * Initial fetch on mount / auth change
   */
  useEffect(() => {
    if (!loadingAuth && authToken) {
      fetchActiveJobs();
    }
  }, [loadingAuth, authToken, fetchActiveJobs]);

  /**
   * Re-fetch on socket reconnect to recover updates missed during disconnection
   */
  useEffect(() => {
    if (isSocketConnected && !prevConnectedRef.current && authToken) {
      fetchActiveJobs();
    }
    prevConnectedRef.current = isSocketConnected;
  }, [isSocketConnected, authToken, fetchActiveJobs]);

  /**
   * WebSocket event handlers (folder/collection-level RAG jobs + file-level enrichment).
   * File-level counter uses polling (source of truth); file_rag_status only
   * enriches progressDetail (cosmetic, intra-file progress pings from N8N).
   */
  useEffect(() => {
    if (!socket) return;

    const TERMINAL_STATUSES = ['completed', 'completed_with_errors', 'failed', 'cancelled', 'dispatch_failed', 'credit_exhausted'];

    const handleRagProgress = (data) => {
      if (!isMountedRef.current) return;

      setActiveJobs((prev) => prev.map((prevJob) => {
        if (prevJob.id !== data.masterJobId) return prevJob;

        if (TERMINAL_STATUSES.includes(prevJob.status)) return prevJob;

        if (prevJob.folder_jobs) {
          const updatedFolderJobs = prevJob.folder_jobs.map((folderJob) => {
            if (folderJob.id === data.folderJobId) {
              const newProcessed = data.filesProcessed != null
                ? data.filesProcessed
                : folderJob.processed_files;
              return {
                ...folderJob,
                status: data.status,
                processed_files: newProcessed,
                total_files: data.filesTotal ?? folderJob.total_files,
                // #1006: el evento trae `errorCode`, no el mensaje. Esta era la vía por la
                // que el crudo de N8N (workflow, nodo, execId) llegaba a la tarjeta ANTES
                // de cualquier refetch HTTP — la «ventana» que se observó en el panel.
                error_code: data.errorCode || folderJob.error_code,
                updated_at: new Date().toISOString()
              };
            }
            return folderJob;
          });

          return {
            ...prevJob,
            status: 'processing',
            folder_jobs: updatedFolderJobs,
            updated_at: new Date().toISOString()
          };
        }
        return prevJob;
      }));
    };

    const handleRagComplete = (data) => {
      if (!isMountedRef.current) return;

      setActiveJobs((prev) => prev.map((prevJob) => {
        if (prevJob.id !== data.masterJobId) return prevJob;
        const failedFiles = data.failedFiles || 0;
        return {
          ...prevJob,
          // El backend completa el job aunque haya ficheros fallidos (el sync sí
          // terminó); failedFiles>0 lo marca como éxito parcial para que el panel
          // no muestre un falso "completada".
          status: failedFiles > 0 ? 'completed_with_errors' : (data.status || 'completed'),
          failed_files: failedFiles,
          error_code: data.errorCode || null,
          completed_at: data.completedAt || new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }));

      // Cierra el chip de reintento SharePoint atado a este master job (#250).
      // failedFiles>0 o status:'failed' ⇒ sube el contador de errores → el banner
      // se refresca. No depende del polling (ciego a ficheros SharePoint).
      closeFileRagJobByMasterJob(data.masterJobId, {
        hadError: (data.failedFiles || 0) > 0 || data.status === 'failed'
      });
    };

    const handleRagCancelled = (data) => {
      if (!isMountedRef.current) return;

      setCancelling(false);
      setActiveJobs((prev) => prev.map((prevJob) => {
        if (prevJob.id !== data.masterJobId) return prevJob;
        return {
          ...prevJob,
          // #1294: con `stopReason` el corte fue por saldo, no un ⏹ del usuario.
          status: data.stopReason ? 'credit_exhausted' : 'cancelled',
          stop_reason: data.stopReason || null,
          cancelled_at: data.cancelledAt,
          completed_folders: data.completedFolders,
          total_folders: data.totalFolders,
          processed_files: data.processedFiles,
          total_files: data.totalFiles
        };
      }));
      // Cancelación no es error: cierra el chip SP sin tocar el contador (#250).
      closeFileRagJobByMasterJob(data.masterJobId, { hadError: false });
      if (data.stopReason) {
        dispatchCreditStop({ reason: data.stopReason, surface: 'vectorization' });
      }
    };

    const handleCollectionDescriptionUpdated = (data) => {
      if (!isMountedRef.current) return;
      console.log('[RagJobContext] collection_description_updated:', data);
      setLastDescriptionUpdate(data);
    };

    // Enriquecimiento efímero del tracker file-level (ping intra-fichero desde N8N).
    // El contador X/N y el cierre del tracker siguen viniendo SOLO del polling.
    const handleFileRagStatus = (data) => {
      if (!isMountedRef.current) return;
      const { rolId, progress, status } = data || {};
      if (!rolId) return;
      // Evento terminal de error de fichero (RAG SharePoint/rol): sube el contador
      // de errores, lo que dispara refreshKnowledge en KnowledgeManagerPage → el
      // banner de error aparece sin recargar. No depende del polling (que no
      // arranca en el sync inicial de SharePoint, solo en el reintento).
      if (status === 'error') {
        setFileRagErrorCount((c) => c + 1);
        return;
      }
      // Aviso NO-fatal: fichero completado pero con imágenes sin describir (#251).
      // Sube el contador → dispara refreshKnowledge en KnowledgeManagerPage → el
      // badge ámbar aparece sin recargar (mismo patrón que el contador de errores).
      if (status === 'completed' && data.warnings) {
        setFileRagWarningCount((c) => c + 1);
        return;
      }
      if (!progress) return;
      setFileRagJobs(prev => {
        if (!prev[rolId]) return prev; // solo enriquece trackers activos — NUNCA crea
        return { ...prev, [rolId]: { ...prev[rolId], progressDetail: { ...progress, ts: Date.now() } } };
      });
    };

    // Mismo evento que handleFileRagStatus (#251), dueño distinto: alimenta el
    // panel de carpetas SharePoint (Sistema A), que no tiene tracker en fileRagJobs.
    // Sin filtrar por rolId aquí — el consumidor (GlobalRagProgressPanel) ya tiene
    // activeJob.rol_id y compara. Filtrar aquí exigiría un activeJobRef nuevo.
    const handleFolderProgressDetail = (data) => {
      if (!isMountedRef.current || !data?.rolId || !data.progress) return;
      setFolderProgressDetail({ rolId: data.rolId, ...data.progress });
    };

    // Timeout / job huérfano: ragOrphanJobDetector marca el job failed y emite
    // rag_error. Sin este oyente los timeouts eran completamente mudos en la UI.
    const handleRagError = (data) => {
      if (!isMountedRef.current) return;
      setActiveJobs((prev) => prev.map((prevJob) => {
        if (prevJob.id !== data.masterJobId) return prevJob;
        if (TERMINAL_STATUSES.includes(prevJob.status)) return prevJob;
        return {
          ...prevJob,
          status: 'failed',
          // #1006: el código lo pone el backend; 'TIMEOUT' es el del catálogo para este
          // camino. El texto en español lo resuelve el panel desde `rag_error_codes`.
          error_code: data.errorCode || 'TIMEOUT',
          updated_at: new Date().toISOString()
        };
      }));
      // Timeout/huérfano: cierra el chip SP atado a este master job como error (#250).
      closeFileRagJobByMasterJob(data.masterJobId, { hadError: true });
    };

    // F13/#304/#306 — alertas de conexión RAG (cuarentena/cambio de contexto/contenido nuevo/
    // borrado pendiente). Un solo listener genérico: cada evento trae `collectionId` y se
    // guarda como el ÚLTIMO estado conocido de esa colección (mínimo funcional v1 — no
    // acumula historial, CollectionCard solo necesita "¿hay algo que revisar ahora?").
    const handleConnectionAlert = (type) => (data) => {
      if (!isMountedRef.current || !data?.collectionId) return;
      // receivedAt (#1416): la tarjeta compara con la hora del fetch de la lista para saber
      // quién manda, el aviso o el agregado; un aviso anterior al fetch no puede taparlo.
      setConnectionAlerts(prev => ({ ...prev, [data.collectionId]: { type, ...data, receivedAt: Date.now() } }));
    };
    const handleContextChanged = handleConnectionAlert('context_changed');
    const handleDeletionQuarantined = handleConnectionAlert('deletion_quarantined');
    const handleNewContent = handleConnectionAlert('new_content');
    const handleDeletionPending = handleConnectionAlert('deletion_pending');

    socket.on('rag_progress', handleRagProgress);
    socket.on('rag_complete', handleRagComplete);
    socket.on('rag_cancelled', handleRagCancelled);
    socket.on('collection_description_updated', handleCollectionDescriptionUpdated);
    socket.on('file_rag_status', handleFileRagStatus);
    socket.on('file_rag_status', handleFolderProgressDetail);
    socket.on('rag_error', handleRagError);
    socket.on('rag_context_changed', handleContextChanged);
    socket.on('rag_deletion_quarantined', handleDeletionQuarantined);
    socket.on('rag_new_content', handleNewContent);
    socket.on('rag_deletion_pending', handleDeletionPending);

    return () => {
      socket.off('rag_progress', handleRagProgress);
      socket.off('rag_complete', handleRagComplete);
      socket.off('rag_cancelled', handleRagCancelled);
      socket.off('collection_description_updated', handleCollectionDescriptionUpdated);
      socket.off('file_rag_status', handleFileRagStatus);
      socket.off('file_rag_status', handleFolderProgressDetail);
      socket.off('rag_error', handleRagError);
      socket.off('rag_context_changed', handleContextChanged);
      socket.off('rag_deletion_quarantined', handleDeletionQuarantined);
      socket.off('rag_new_content', handleNewContent);
      socket.off('rag_deletion_pending', handleDeletionPending);
    };
  }, [socket, closeFileRagJobByMasterJob]);

  /**
   * Clear state on SIGNED_OUT (security: prevent data leakage)
   */
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setActiveJobs([]);
        setCancelling(false);
        setFileRagJobs({});
        setFileRagCompletionCount(0);
        setFileRagErrorCount(0);
        setFileRagWarningCount(0);
        setConnectionAlerts({});
      }
    });
    return () => subscription?.unsubscribe();
  }, []);

  /**
   * Cancel an active job
   */
  const cancelJob = useCallback(async (jobId) => {
    setCancelling(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/rag-jobs/${jobId}/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (!data.success) {
        setCancelling(false);
        return { success: false, error: data.message };
      }
      return { success: true };
    } catch (err) {
      setCancelling(false);
      return { success: false, error: err.message };
    }
  }, [authToken]);

  /**
   * Start tracking — triggers a refetch to pick up newly created jobs
   */
  const startTracking = useCallback(() => {
    fetchActiveJobs();
  }, [fetchActiveJobs]);

  /**
   * Stop tracking (used on dismiss). With a jobId, dismisses only that master's
   * bar; without one, clears all (back-compat with the old single-job dismiss).
   */
  const stopTracking = useCallback((jobId = null) => {
    if (jobId == null) {
      setActiveJobs([]);
      return;
    }
    setActiveJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  /**
   * Descarta la alerta de conexión de una colección (F13/#304/#306) — tras resolverla
   * (aprobar/rechazar cuarentena, mantener/desvincular contexto) o al cerrar el banner.
   * Con `expected`, solo si el aviso actual sigue siendo ese: uno más nuevo, llegado mientras
   * duraba la petición de «Mantener»/«Desvincular», no se descarta (#1416).
   */
  const dismissConnectionAlert = useCallback((collectionId, expected) => {
    setConnectionAlerts(prev => {
      if (!prev[collectionId]) return prev;
      if (expected && prev[collectionId] !== expected) return prev;
      const { [collectionId]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Start tracking file RAG vectorization for a role.
   * Takes a snapshot of current completed/error counts so the chip shows
   * THIS BATCH's progress, not the role's all-time total.
   * Fire-and-forget (callers don't need to await).
   */
  const startFileRagTracking = useCallback(async (rolId, totalFiles, collectionId = null, opts = {}) => {
    let baseCompleted = 0;
    let baseError = 0;

    if (authToken) {
      try {
        const res = await fetch(`${API_BASE_URL}/api/roles/${rolId}/knowledge/status`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        if (res.ok) {
          const body = await res.json();
          const stats = body?.data?.fileStats;
          if (stats) {
            baseCompleted = stats.completed || 0;
            baseError = stats.error || 0;
          }
        }
      } catch (_err) {
        // Snapshot failed — base stays 0, chip may show absolute counts for this batch
      }
    }

    if (!isMountedRef.current) return;

    setFileRagJobs(prev => ({
      ...prev,
      [rolId]: {
        total: totalFiles,
        completed: 0,
        errors: 0,
        collectionId,
        _base_completed: baseCompleted,
        _base_error: baseError,
        // Trackers reconstruidos tras recarga: el polling puede cerrarlos sin
        // exigir delta>0 (cubre la carrera "el fichero completó durante el F5")
        _rehydrated: !!opts.rehydrated,
        // Reintento SharePoint (#250): el chip se cierra por el ciclo de vida del
        // master job (eventos WS terminales + reconciliación), NO por el polling de
        // fileStats — los ficheros SharePoint no existen en la tabla `file`.
        masterJobId: opts.masterJobId || null,
        createdAt: Date.now(),
      }
    }));
  }, [authToken]);

  /**
   * Clear a role's file RAG tracker without signalling completion.
   * Used when the caller knows no progress events will arrive (sync skipped or failed).
   */
  const clearFileRagTracking = useCallback((rolId) => {
    setFileRagJobs(prev => {
      if (!prev[rolId]) return prev;
      const { [rolId]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  /**
   * Rebuild file RAG trackers after a page reload or socket reconnect.
   * DB is the source of truth: any rol owned by the user with files still
   * pending/processing gets its tracker back. The rebuilt tracker shows the
   * REMAINING work (in-flight files), not the original upload batch.
   * Never overwrites a live tracker (e.g. one just created by an upload).
   */
  const rehydrateFileRagJobs = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/roles/active-vectorizations`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!res.ok || !isMountedRef.current) return;
      const body = await res.json();
      const active = body?.data?.active || [];
      for (const { rolId, inFlight } of active) {
        if (!rolId || !Number.isInteger(inFlight) || inFlight <= 0) continue;
        if (fileRagJobsRef.current[rolId]) continue; // never clobber a live tracker
        startFileRagTracking(rolId, inFlight, null, { rehydrated: true });
      }
    } catch (_err) {
      // Transient network error — next reload/reconnect retries
    }
  }, [authToken, startFileRagTracking]);

  /**
   * Rehydrate on mount (page reload) and on socket reconnect.
   * Mirrors the fetchActiveJobs effects above for folder/collection jobs.
   */
  useEffect(() => {
    if (!loadingAuth && authToken) {
      rehydrateFileRagJobs();
    }
  }, [loadingAuth, authToken, rehydrateFileRagJobs]);

  useEffect(() => {
    if (isSocketConnected && !prevRehydrateConnectedRef.current && authToken) {
      rehydrateFileRagJobs();
    }
    prevRehydrateConnectedRef.current = isSocketConnected;
  }, [isSocketConnected, authToken, rehydrateFileRagJobs]);

  /**
   * Polling loop: checks DB-derived progress every 5 s while any trackers are active.
   * Single source of truth — no incremental WebSocket counters.
   */
  const hasFileRagJobs = Object.keys(fileRagJobs).length > 0;
  useEffect(() => {
    if (!hasFileRagJobs || !authToken) return undefined;

    const poll = async () => {
      const jobs = fileRagJobsRef.current;
      for (const rolId of Object.keys(jobs)) {
        // Trackers de reintento SharePoint (con masterJobId) los cierra el ciclo de
        // vida del master job, no el polling: su fichero nunca está en la tabla
        // `file` que cuenta /knowledge/status, así que el poll jamás los cerraría (#250).
        if (jobs[rolId]?.masterJobId) continue;
        try {
          const res = await fetch(`${API_BASE_URL}/api/roles/${rolId}/knowledge/status`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          if (!isMountedRef.current || !res.ok) continue;
          const body = await res.json();
          const stats = body?.data?.fileStats;
          if (!stats) continue;

          const job = fileRagJobsRef.current[rolId];
          if (!job) continue;

          const deltaCompleted = Math.max(0, stats.completed - (job._base_completed || 0));
          const deltaError = Math.max(0, stats.error - (job._base_error || 0));
          const allDone = (deltaCompleted + deltaError) >= job.total
            || (stats.pending === 0 && stats.processing === 0 && stats.total > 0 && deltaCompleted + deltaError > 0)
            // Trackers rehidratados: cerrar también con delta 0 si nada sigue en vuelo
            // (el fichero pudo completar entre la query de rehidratación y el snapshot)
            || (job._rehydrated && stats.pending === 0 && stats.processing === 0);

          if (allDone) {
            if (deltaError > 0) setFileRagErrorCount(c => c + 1);
            setFileRagJobs(prev => {
              if (!prev[rolId]) return prev;
              const { [rolId]: _removed, ...rest } = prev;
              return rest;
            });
            setFileRagCompletionCount(c => c + 1);
          } else {
            setFileRagJobs(prev => {
              if (!prev[rolId]) return prev;
              // Si el contador avanzó, el fichero cuyo progressDetail mostrábamos ya
              // terminó: soltar el detalle cosmético (contador y cierre no se tocan).
              const advanced = deltaCompleted > (prev[rolId].completed || 0);
              return {
                ...prev,
                [rolId]: {
                  ...prev[rolId],
                  completed: deltaCompleted,
                  errors: deltaError,
                  ...(advanced ? { progressDetail: undefined } : {})
                }
              };
            });
          }
        } catch (_err) {
          // Transient network error — retry next cycle
        }
      }
    };

    // Immediate first poll so the chip updates without waiting a full interval
    poll();
    const intervalId = setInterval(poll, FILE_RAG_POLL_MS);
    return () => clearInterval(intervalId);
  }, [hasFileRagJobs, authToken]);

  const TERMINAL = ['completed', 'completed_with_errors', 'failed', 'cancelled', 'dispatch_failed', 'credit_exhausted'];
  // Back-compat alias: consumers/tests that read a single job get the first one.
  const activeJob = activeJobs.length > 0 ? activeJobs[0] : null;
  const hasActiveJob = activeJobs.some((j) => !TERMINAL.includes(j.status));

  const value = useMemo(() => ({
    activeJobs,
    activeJob,
    hasActiveJob,
    cancelling,
    cancelJob,
    startTracking,
    stopTracking,
    refetch: fetchActiveJobs,
    socket,
    isSocketConnected,
    lastDescriptionUpdate,
    folderProgressDetail,
    fileRagJobs,
    fileRagCompletionCount,
    fileRagErrorCount,
    fileRagWarningCount,
    startFileRagTracking,
    clearFileRagTracking,
    rehydrateFileRagJobs,
    connectionAlerts,
    dismissConnectionAlert
  }), [activeJobs, activeJob, hasActiveJob, cancelling, cancelJob, startTracking, stopTracking, fetchActiveJobs, socket, isSocketConnected, lastDescriptionUpdate, folderProgressDetail, fileRagJobs, fileRagCompletionCount, fileRagErrorCount, fileRagWarningCount, startFileRagTracking, clearFileRagTracking, rehydrateFileRagJobs, connectionAlerts, dismissConnectionAlert]);

  return (
    <RagJobContext.Provider value={value}>
      {children}
    </RagJobContext.Provider>
  );
};

/**
 * Hook to consume the RagJob context.
 * Must be used within a RagJobProvider.
 */
export const useRagJob = () => {
  const context = useContext(RagJobContext);
  if (context === null) {
    throw new Error('useRagJob must be used within a RagJobProvider');
  }
  return context;
};

export default RagJobContext;
